import React from 'react';

import { Box, Checkbox } from '@mui/material';

import Image from '~/components/Common/Image';

interface ImportableImage {
  id: string;
  imageId: string;
  title: string | null;
}

interface Props {
  images: Array<ImportableImage>;
  existingImageIdSet: Set<string>;
  selectedImageIds: Array<string>;
  onToggleSelected: (imageId: string) => void;
}

export default function ImportableImageGrid({ images, existingImageIdSet, selectedImageIds, onToggleSelected }: Props) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(2, minmax(0, 1fr))',
          md: 'repeat(3, minmax(0, 1fr))',
        },
        gap: 2,
      }}
    >
      {images.map((image) => {
        const isAlreadyImported = existingImageIdSet.has(image.imageId);
        const isSelected = selectedImageIds.includes(image.imageId);

        return (
          <Box
            key={image.id}
            sx={{
              position: 'relative',
              overflow: 'hidden',
              aspectRatio: '16 / 9',
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              bgcolor: 'background.default',
              cursor: isAlreadyImported ? 'not-allowed' : 'pointer',
            }}
            onClick={() => !isAlreadyImported && onToggleSelected(image.imageId)}
          >
            <Image
              publicId={image.imageId}
              alt={image.title || 'Property image'}
              draggable={false}
              loading="lazy"
              options={{ width: 320, height: 180 }}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block',
                filter: isAlreadyImported ? 'grayscale(1) opacity(0.42)' : undefined,
              }}
            />
            <Checkbox
              checked={isSelected || isAlreadyImported}
              disabled={isAlreadyImported}
              size="small"
              sx={{
                position: 'absolute',
                top: 4,
                right: 4,
                bgcolor: 'common.white',
                borderRadius: '4px',
                p: 0.25,
                '&:hover': { bgcolor: 'grey.100' },
              }}
              onClick={(event) => event.stopPropagation()}
              onChange={() => onToggleSelected(image.imageId)}
            />
          </Box>
        );
      })}
    </Box>
  );
}
